/**
 * @name CommentDialog
 * @description Modal de solicitud de cambio de puesto
 * @author Alexis Becerra
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Icons, Layout, MUI, Typography } from 'styles';

const values = {
  comment: ''
};

const schema = Yup.object({
  comment: Yup.string()
    .min(2, <Trans>FormsGeneralTexts.min2Chars</Trans>)
    .max(500, <Trans>FormsGeneralTexts.max500Chars</Trans>)
    .required('*')
});

const CommentDialog = (props) => {
  const {
    action,
    open,
    onClose,
    onRejectConfirmation,
    onReturnConfirmation
  } = props;

  return (
    <Formik
      initialValues={values}
      validationSchema={schema}
      isInitialValid={schema.isValidSync(values)}
      onSubmit={(values, { resetForm, setSubmitting }) => {
        action === 'reject' ? onRejectConfirmation(values, resetForm, setSubmitting) : onReturnConfirmation(values, resetForm, setSubmitting);
      }}
    >
      {(props) => (
        <MUI.Dialog
          open={open}
          onClose={onClose}
          maxWidth="sm"
        >
          <MUI.DialogTitle>
            {action === 'reject' ? (
              <Trans>Request.rejectRequest</Trans>
            ) : (
                <Trans>Request.approveRequest</Trans>
              )}
            <MUI.DialogBoxClose onClick={onClose}>
              <Icons.Close />
            </MUI.DialogBoxClose>
          </MUI.DialogTitle>

          <MUI.DialogContent>
            <MUI.Box padding="8px">
              <Typography.H6>
                {action === 'reject' ? (
                  <Trans>Request.whyRejectingRequest</Trans>
                ) : (
                    <Trans>Request.whyApproveRequest</Trans>
                  )}
              </Typography.H6>
              <Typography.Text><Trans>Request.warningRequest</Trans></Typography.Text>
            </MUI.Box>
            {/* Comentario */}
            <MUI.Box padding="8px">
              <MUI.TextField
                multiline
                label={<Trans>GenericMessages.comment</Trans>}
                type="text"
                name="comment"
                value={props.values.comment}
                characterLimit={500}
                onChange={props.handleChange}
                error={Boolean(props.errors.comment)}
                helperText={props.errors.comment}
                inputProps={{
                  maxlength: '500',
                }}
              />
            </MUI.Box>
          </MUI.DialogContent>

          <MUI.DialogActions>
            <Layout.BoxRequired>
              {`* `}
              <Trans>FormsGeneralTexts.required</Trans>
            </Layout.BoxRequired>
            <MUI.ButtonText onClick={onClose}>
              <Trans>DialogConfirm.cancel</Trans>
            </MUI.ButtonText>
            <MUI.ButtonCTA
              type="button"
              color="primary"
              disabled={!props.isValid || props.isSubmitting}
              onClick={props.handleSubmit}
            >
              {action === 'reject' ? (
                <Trans>DialogConfirm.reject</Trans>
              ) : (
                  <Trans>DialogConfirm.approve</Trans>
                )}
            </MUI.ButtonCTA>
          </MUI.DialogActions>
        </MUI.Dialog>
      )}
    </Formik>
  );
};

CommentDialog.propTypes = {
  // String que indica la acción de modal (Retornar estatus o rechazar)
  action: PropTypes.oneOf(['return', 'reject']),
  // Boolean que indica si el modal esta abierto.
  open: PropTypes.bool.isRequired,
  // Función que se ejecuta cuando se cierra el modal.
  onClose: PropTypes.func.isRequired,
  // Función que se ejecuta cuando se rechaza las responsabilidades.
  onRejectConfirmation: PropTypes.func.isRequired,
  // Función que se ejecuta cuando se retorna el estatus a activo de las responsabilidades.
  onReturnConfirmation: PropTypes.func.isRequired,
  // Formik props.
  values: PropTypes.any,
  errors: PropTypes.any,
  isValid: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  handleChange: PropTypes.func,
  handleSubmit: PropTypes.func,
};

export default CommentDialog;
